import React from 'react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <section className="bg-gradient-to-br from-primary-50 to-white py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-2">
              Privacy <span className="text-primary">Policy</span>
            </h1>
            <p className="text-lg text-gray-600 font-medium">Checkstall China Import</p>
            {/* <p className="text-gray-500 text-sm italic mt-2">
              Last Updated: {new Date().toLocaleDateString()}
            </p> */}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-soft-lg p-8 md:p-12">
            <div className="prose prose-lg max-w-none text-gray-700">
              <p className="mb-6 leading-relaxed">
                At Checkstall China Import, we are committed to protecting your privacy and personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform and services. This policy is designed to comply with the Nigeria Data Protection Regulation (NDPR) 2019 and other applicable data protection laws.
              </p>
              <p className="mb-10 leading-relaxed">
                By using our services, you consent to the data practices described in this policy. If you do not agree with this policy, please do not use our services.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">1. Information We Collect</h2>
              <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-2">1.1 Personal Information</h3>
              <p className="mb-2">We collect information that you provide directly to us, including:</p>
              <ul className="list-disc pl-6 mb-6 space-y-1">
                <li>Name, email address, phone number, and contact information</li>
                <li>Shipping addresses and delivery information</li>
                <li>Payment information (processed securely through third-party payment processors)</li>
                <li>Account credentials (username, password)</li>
                <li>Order history and transaction records</li>
                <li>Customer service communications</li>
                <li>Preferences and settings</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-2">1.2 Automatically Collected Information</h3>
              <p className="mb-2">When you use our services, we automatically collect certain information, including:</p>
              <ul className="list-disc pl-6 mb-6 space-y-1">
                <li>IP address and device identifiers</li>
                <li>Browser type and version</li>
                <li>Operating system information</li>
                <li>Usage data and interaction patterns</li>
                <li>Location data (with your consent)</li>
                <li>Log files and analytics data</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-2">1.3 Information from Third Parties</h3>
              <p className="mb-6">
                We may receive information about you from third-party services, including payment processors, shipping providers, and analytics services, in accordance with their privacy policies.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">2. How We Use Your Information</h2>
              <p className="mb-2">We use the information we collect for the following purposes:</p>
              <ul className="list-disc pl-6 mb-6 space-y-1">
                <li>To provide, maintain, and improve our services</li>
                <li>To process transactions and manage orders</li>
                <li>To communicate with you about your account, orders, and services</li>
                <li>To send you marketing communications (with your consent)</li>
                <li>To personalize your experience and provide relevant content</li>
                <li>To detect, prevent, and address fraud, security, or technical issues</li>
                <li>To comply with legal obligations and enforce our terms</li>
                <li>To conduct research and analytics to improve our services</li>
              </ul>

              <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">3. Legal Basis for Processing</h2>
              <p className="mb-2">We process your personal information based on the following legal grounds:</p>
              <ul className="list-disc pl-6 mb-6 space-y-1">
                <li><strong>Consent:</strong> When you have given clear consent for specific processing activities</li>
                <li><strong>Contract Performance:</strong> To fulfill our contractual obligations to you</li>
                <li><strong>Legal Obligation:</strong> To comply with applicable laws and regulations</li>
                <li><strong>Legitimate Interests:</strong> For our legitimate business interests, such as fraud prevention and service improvement</li>
              </ul>

              <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">4. Information Sharing and Disclosure</h2>
              <p className="mb-4">We do not sell your personal information. We may share your information in the following circumstances:</p>
              <h3 className="text-xl font-semibold text-gray-900 mt-4 mb-2">4.1 Service Providers</h3>
              <p className="mb-4">
                We share information with trusted third-party service providers who assist us in operating our platform, including payment processors, shipping companies, customer support services, and analytics providers. These providers are contractually obligated to protect your information and use it only for the purposes we specify.
              </p>
              <h3 className="text-xl font-semibold text-gray-900 mt-4 mb-2">4.2 Business Partners</h3>
              <p className="mb-4">With your consent, we may share information with business partners to provide you with relevant products, services, or promotional offers.</p>
              <h3 className="text-xl font-semibold text-gray-900 mt-4 mb-2">4.3 Legal Requirements</h3>
              <p className="mb-4">
                We may disclose your information if required by law, court order, or governmental authority, or to protect our rights, property, or safety, or that of our users or others.
              </p>
              <h3 className="text-xl font-semibold text-gray-900 mt-4 mb-2">4.4 Business Transfers</h3>
              <p className="mb-6">
                In the event of a merger, acquisition, or sale of assets, your information may be transferred to the acquiring entity, subject to the same privacy protections.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">5. International Data Transfers</h2>
              <p className="mb-2">
                Your information may be transferred to and processed in countries other than your country of residence, including China and Nigeria. We ensure that appropriate safeguards are in place to protect your information, including standard contractual clauses, adequate security measures and encryption, and compliance with applicable data protection regulations.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">6. Data Security</h2>
              <p className="mb-2">We implement appropriate technical and organizational measures to protect your personal information, including:</p>
              <ul className="list-disc pl-6 mb-4 space-y-1">
                <li>Encryption of sensitive data in transit and at rest</li>
                <li>Secure authentication and access controls</li>
                <li>Regular security assessments and updates</li>
                <li>Employee training on data protection</li>
                <li>Incident response procedures</li>
              </ul>
              <p className="mb-6">
                However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">7. Data Retention</h2>
              <p className="mb-2">
                We retain your personal information for as long as necessary to fulfill the purposes outlined in this policy, unless a longer retention period is required or permitted by law. When information is no longer needed, we will securely delete or anonymize it.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">8. Your Rights Under NDPR 2019</h2>
              <p className="mb-4">As a data subject, you have the following rights regarding your personal information:</p>
              <ul className="list-none space-y-3 mb-6">
                <li><strong>Right of Access:</strong> You have the right to request access to your personal information and receive a copy of the data we hold about you.</li>
                <li><strong>Right to Rectification:</strong> You have the right to request correction of inaccurate or incomplete personal information.</li>
                <li><strong>Right to Erasure (Right to be Forgotten):</strong> You have the right to request deletion of your personal information when it is no longer necessary, subject to legal obligations.</li>
                <li><strong>Right to Restrict Processing:</strong> You have the right to request restriction of processing in certain circumstances.</li>
                <li><strong>Right to Data Portability:</strong> You have the right to receive your personal information in a structured, commonly used, and machine-readable format.</li>
                <li><strong>Right to Object:</strong> You have the right to object to processing for direct marketing or based on legitimate interests.</li>
                <li><strong>Right to Withdraw Consent:</strong> Where processing is based on consent, you have the right to withdraw your consent at any time.</li>
              </ul>
              <p className="mb-6">
                To exercise any of these rights, please contact us using the information provided below. We will respond to your request within 30 days, in accordance with NDPR requirements.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">9. Marketing Communications</h2>
              <p className="mb-2">
                With your consent, we may send you marketing communications. You can opt-out at any time by clicking the unsubscribe link in any marketing email, updating your preferences in your account settings, or contacting us at support@checkstall.com. Please note that we may still send you transactional and service-related messages.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">10. Data Breach Notification</h2>
              <p className="mb-2">In the event of a data breach that is likely to result in a high risk to your rights and freedoms, we will notify you without undue delay (within 72 hours of becoming aware), notify the relevant data protection authority as required by law, and provide clear information about the nature of the breach and measures taken.</p>

              <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">11. Children&apos;s Privacy</h2>
              <p className="mb-6">
                Our services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">12. Third-Party Links and Services</h2>
              <p className="mb-6">
                Our platform may contain links to third-party websites or services. We are not responsible for the privacy practices of these third parties. We encourage you to review the privacy policies of any third-party services you access through our platform.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">13. Changes to This Privacy Policy</h2>
              <p className="mb-10">
                We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the updated policy on our platform and updating the &quot;Last Updated&quot; date. Your continued use of our services after such changes constitutes acceptance of the updated policy.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">Contact Information</h2>
              <p className="mb-2">If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:</p>
              <div className="bg-gray-50 rounded-2xl p-6 mb-4">
                <p className="font-semibold text-gray-900">Checkstall China Import</p>
                <p className="mt-2">Email: privacy@checkstall.com</p>
                <p>General Support: support@checkstall.com</p>
                <p>Customer Service: Available through the app</p>
              </div>
              <p className="mb-6">For data protection inquiries or to exercise your rights, please include &quot;Privacy Request&quot; in the subject line of your email.</p>

              <div className="border-t border-gray-200 pt-6 mt-10">
                <p className="text-sm text-gray-500 italic text-center">
                  This Privacy Policy is effective as of the date listed above and applies to all users of Checkstall China Import services.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
